/**
 * CARI Zustand store.
 *
 * State management for the CARI panel that lives inside Phreatic. Mirrors
 * the TERRA store's shape so the panel UI patterns lift cleanly, but
 * scoped to CARI's surface — no Data Vault bridge, no UI navigation
 * actions, no highlights. CARI is a reasoning agent that produces text,
 * tool results, and inline components; she doesn't steer the surrounding
 * UI the way TERRA does.
 */
import { create } from 'zustand';
import type {
  CARIConversation,
  CARIMessage,
  Citation,
  InlineComponent,
} from '@/api/cari';
import {
  createConversation,
  listConversations,
  loadConversation,
  deleteConversation as apiDeleteConversation,
  renameConversation as apiRenameConversation,
  streamMessage,
} from '@/api/cari';

// ── Tool-call display state ────────────────────────────────────────────

export interface ActiveToolCall {
  name: string;
  input: Record<string, unknown>;
  isActive: boolean;
  summary?: string;
  durationMs?: number;
}

// ── Default suggestions ───────────────────────────────────────────────
// Replaces the previous per-profile suggestion sets when CARI was unified
// to match TERRA's model 2026-06. The agent still emits per-conversation
// suggestions; this list seeds new conversations until it does.

const DEFAULT_SUGGESTIONS: string[] = [
  'What are the current alerts and what should I do about them?',
  'Pull the latest water-availability assessment for this site.',
  'Forecast supply vs demand over the next 10 years.',
  'Generate a Lender Groundwater Assessment for this property.',
  'Show me cross-aquifer trends in the last 5 years.',
];

// ── Store interface ────────────────────────────────────────────────────

interface CARIStore {
  // Panel state
  isOpen: boolean;
  isPinned: boolean;

  // Conversations
  conversations: CARIConversation[];
  activeConversationId: string | null;

  // Messages for active conversation
  messages: CARIMessage[];

  // Streaming state
  isStreaming: boolean;
  streamingText: string;
  activeToolCalls: ActiveToolCall[];

  // Inline citations + components emitted during the current stream
  citations: Citation[];
  pendingComponents: InlineComponent[];

  // Reasoning trace (collapsed by default in the UI)
  thinkingText: string;

  // Rate-limit countdown (timestamp ms when limit expires)
  rateLimitUntil: number | null;

  // Suggestion chips
  suggestions: string[];

  // ── Actions ─────────────────────────────────────────────────────────
  toggle: () => void;
  open: () => void;
  close: () => void;
  pin: () => void;
  unpin: () => void;

  // Conversation management
  fetchConversations: (siteId?: string) => Promise<void>;
  newConversation: (siteId?: string) => Promise<string>;
  switchConversation: (id: string) => Promise<void>;
  renameConversationLocal: (id: string, title: string) => Promise<void>;
  removeConversation: (id: string) => Promise<void>;

  // Messaging
  sendMessage: (content: string, context?: Record<string, unknown>) => void;
  cancelStream: () => void;
}

// Track the active stream's AbortController across renders so cancel
// works from any component without prop-drilling.
let _abortController: AbortController | null = null;

export const useCARIStore = create<CARIStore>((set, get) => ({
  // ── Initial state ───────────────────────────────────────────────────
  isOpen: false,
  isPinned: false,

  conversations: [],
  activeConversationId: null,

  messages: [],

  isStreaming: false,
  streamingText: '',
  activeToolCalls: [],
  citations: [],
  pendingComponents: [],
  thinkingText: '',
  rateLimitUntil: null,
  suggestions: DEFAULT_SUGGESTIONS,

  // ── Panel state ─────────────────────────────────────────────────────
  toggle: () => set((s) => ({ isOpen: !s.isOpen })),
  open: () => set({ isOpen: true }),
  close: () => set((s) => (s.isPinned ? {} : { isOpen: false })),
  pin: () => set({ isPinned: true }),
  unpin: () => set({ isPinned: false }),

  // ── Conversation management ─────────────────────────────────────────
  fetchConversations: async (siteId?: string) => {
    try {
      const convos = await listConversations(siteId);
      set({ conversations: convos });
    } catch (err) {
      console.error('CARI: failed to list conversations', err);
    }
  },

  newConversation: async (siteId?: string) => {
    try {
      const conv = await createConversation(siteId);
      set((s) => ({
        conversations: [conv, ...s.conversations],
        activeConversationId: conv.id,
        messages: [],
        streamingText: '',
        activeToolCalls: [],
        citations: [],
        pendingComponents: [],
        thinkingText: '',
        suggestions: DEFAULT_SUGGESTIONS,
      }));
      return conv.id;
    } catch (err) {
      console.error('CARI: failed to create conversation', err);
      throw err;
    }
  },

  switchConversation: async (id: string) => {
    try {
      const detail = await loadConversation(id);
      set({
        activeConversationId: id,
        messages: detail.messages,
        streamingText: '',
        activeToolCalls: [],
        citations: [],
        pendingComponents: [],
        thinkingText: '',
        suggestions: DEFAULT_SUGGESTIONS,
      });
    } catch (err) {
      console.error('CARI: failed to load conversation', err);
    }
  },

  renameConversationLocal: async (id: string, title: string) => {
    try {
      const updated = await apiRenameConversation(id, title);
      set((s) => ({
        conversations: s.conversations.map((c) =>
          c.id === id ? { ...c, title: updated.title, updated_at: updated.updated_at } : c,
        ),
      }));
    } catch (err) {
      console.error('CARI: failed to rename conversation', err);
    }
  },

  removeConversation: async (id: string) => {
    try {
      await apiDeleteConversation(id);
      set((s) => {
        const convos = s.conversations.filter((c) => c.id !== id);
        const newActive =
          s.activeConversationId === id ? convos[0]?.id ?? null : s.activeConversationId;
        return {
          conversations: convos,
          activeConversationId: newActive,
          messages: s.activeConversationId === id ? [] : s.messages,
        };
      });
    } catch (err) {
      console.error('CARI: failed to delete conversation', err);
    }
  },

  // ── Messaging ───────────────────────────────────────────────────────
  sendMessage: (content: string, context?: Record<string, unknown>) => {
    const { activeConversationId, isStreaming } = get();
    if (!activeConversationId || isStreaming) return;

    // Optimistically append the user message so the UI doesn't blank out
    // for the round-trip.
    const userMsg: CARIMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content,
      tool_name: null,
      tool_input: null,
      tool_output: null,
      ui_actions: null,
      tokens_used: 0,
      latency_ms: null,
      rating: null,
      feedback_text: null,
      created_at: new Date().toISOString(),
    };

    set((s) => ({
      messages: [...s.messages, userMsg],
      isStreaming: true,
      streamingText: '',
      thinkingText: '',
      activeToolCalls: [],
      citations: [],
      pendingComponents: [],
    }));

    // Batch delta updates so React doesn't re-render per token. Flushes
    // every 50ms — same pattern as the TERRA panel.
    let _deltaBuffer = '';
    let _deltaTimer: ReturnType<typeof setTimeout> | null = null;
    const flushDelta = () => {
      if (_deltaBuffer) {
        const text = _deltaBuffer;
        _deltaBuffer = '';
        set((s) => ({ streamingText: s.streamingText + text }));
      }
      _deltaTimer = null;
    };

    _abortController = streamMessage(activeConversationId, content, context ?? null, {
      onDelta: ({ text }) => {
        _deltaBuffer += text;
        if (!_deltaTimer) _deltaTimer = setTimeout(flushDelta, 50);
      },

      onToolStart: ({ tool_name, tool_input }) => {
        flushDelta();
        set((s) => {
          // If we've accumulated streaming text before this tool call,
          // commit it as an assistant message so it survives the
          // tool-call event boundary.
          const newMessages = [...s.messages];
          if (s.streamingText) {
            newMessages.push({
              id: `temp-assistant-${Date.now()}`,
              role: 'assistant',
              content: s.streamingText,
              tool_name: null,
              tool_input: null,
              tool_output: null,
              ui_actions: null,
              tokens_used: 0,
              latency_ms: null,
              rating: null,
              feedback_text: null,
              created_at: new Date().toISOString(),
            });
          }
          return {
            messages: newMessages,
            streamingText: '',
            activeToolCalls: [
              ...s.activeToolCalls,
              { name: tool_name, input: tool_input, isActive: true },
            ],
          };
        });
      },

      onToolEnd: ({ tool_name, summary, duration_ms }) => {
        set((s) => ({
          activeToolCalls: s.activeToolCalls.map((tc, idx) =>
            // Mark the FIRST still-active matching tool as complete. With
            // parallel fan-out from agent_runtime, multiple tools may
            // share a name in the same turn — close them in arrival order.
            tc.name === tool_name && tc.isActive
              ? { ...tc, isActive: false, summary, durationMs: duration_ms }
              : tc,
          ),
        }));
      },

      onThinking: ({ text }) => {
        set((s) => ({ thinkingText: s.thinkingText + text }));
      },

      onUIComponent: (component) => {
        set((s) => ({ pendingComponents: [...s.pendingComponents, component] }));
      },

      onDone: ({ citations }) => {
        flushDelta();
        set((s) => {
          // Finalize any pending streamingText into a real assistant message.
          const newMessages = [...s.messages];
          if (s.streamingText) {
            newMessages.push({
              id: `temp-assistant-${Date.now()}`,
              role: 'assistant',
              content: s.streamingText,
              tool_name: null,
              tool_input: null,
              tool_output: null,
              ui_actions: null,
              tokens_used: 0,
              latency_ms: null,
              rating: null,
              feedback_text: null,
              created_at: new Date().toISOString(),
            });
          }
          return {
            messages: newMessages,
            streamingText: '',
            isStreaming: false,
            citations: citations ?? s.citations,
          };
        });

        // Re-fetch the canonical conversation list so the sidebar's
        // updated_at + message_count reflect the just-finished exchange.
        get()
          .fetchConversations()
          .catch(() => {});
      },

      onError: (err) => {
        flushDelta();
        const rateLimitUntil =
          err.code === 'rate_limited' && err.retry_after_seconds
            ? Date.now() + err.retry_after_seconds * 1000
            : null;
        set({
          isStreaming: false,
          rateLimitUntil,
        });
        console.error('CARI: stream error', err);
      },
    });
  },

  cancelStream: () => {
    if (_abortController) {
      _abortController.abort();
      _abortController = null;
    }
    set({ isStreaming: false });
  },
}));
